import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Reads the currently selected option name/value pairs out of a rendered variant picker,
 * regardless of its display style (buttons, swatches, dropdowns, or a single-value option
 * rendered as plain text) and regardless of how many options the product has.
 *
 * @param {Element} root - A `variant-picker` element (or any ancestor containing one).
 * @returns {[string, string][]} Array of [optionName, selectedValue] pairs.
 */
function getSelectedOptionPairs(root) {
  /** @type {[string, string][]} */
  const pairs = [];

  root.querySelectorAll('.variant-option--buttons, .variant-option--swatches').forEach((fieldset) => {
    const legend = fieldset.querySelector('legend');
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    if (!legend || !checked) return;

    const legendClone = legend.cloneNode(true);
    legendClone.querySelector('.variant-option__swatch-value')?.remove();
    const name = legendClone.textContent?.trim();
    const value = checked.getAttribute('value');
    if (name && value) pairs.push([name, value]);
  });

  root.querySelectorAll('.variant-option--dropdowns').forEach((wrapper) => {
    const label = wrapper.querySelector('label');
    const select = wrapper.querySelector('select');
    const option = select?.selectedOptions?.[0];
    if (!label || !option) return;

    const name = label.textContent?.trim();
    const value = option.getAttribute('value');
    if (name && value) pairs.push([name, value]);
  });

  root
    .querySelectorAll('.variant-option:not(.variant-option--buttons):not(.variant-option--swatches):not(.variant-option--dropdowns)')
    .forEach((div) => {
      const valueEl = div.querySelector('.variant-option__swatch-value');
      if (!valueEl) return;

      const clone = div.cloneNode(true);
      clone.querySelector('.variant-option__swatch-value')?.remove();
      const name = clone.textContent?.trim();
      const value = valueEl.textContent?.trim();
      if (name && value) pairs.push([name, value]);
    });

  return pairs;
}

/**
 * <custom-quote-form> shows a "Request a custom quote" contact form two ways:
 *   - automatically, in place of a dead-end disabled Add to cart button, when the customer's
 *     current combination of option selections matches zero real product variants. It listens
 *     for the theme's `variant:update` event (dispatched by `variant-picker` after each section
 *     re-fetch): shows itself when the event's `resource` is `null` (no variant matches the
 *     current selection), hides itself for every other case including a real variant that's
 *     simply sold out (`resource.available === false`), which keeps the theme's normal sold-out
 *     behavior intact.
 *   - manually, via the "Don't see what you're looking for?" trigger rendered alongside this
 *     element (a sibling, not a descendant, so it stays reachable even while this card is
 *     hidden) - for a shopper who wants an option that was never created as a variant at all,
 *     not just one that's currently unselected.
 *
 * @extends Component
 */
export default class CustomQuoteForm extends Component {
  /** @type {boolean} */
  #userEditedMessage = false;

  /** @type {string} */
  #lastAutoMessage = '';

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    const { messageField } = this.refs;
    if (messageField instanceof HTMLTextAreaElement) {
      this.#lastAutoMessage = messageField.value;
    }

    this.#abortController = new AbortController();
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#abortController.signal,
    });

    // The "Don't see what you're looking for?" trigger is a sibling element (not a descendant
    // of <custom-quote-form>, so it can stay visible even while this card itself is hidden),
    // matched by the same product/section ids rather than DOM position.
    const trigger = document.querySelector(
      `.custom-quote-form__trigger[data-product-id="${this.dataset.productId}"][data-section-id="${this.dataset.sectionId}"]`
    );
    trigger?.addEventListener('click', this.#show, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Marks the message field as manually edited, once its value diverges from the last
   * auto-filled text, so later selection changes stop overwriting it.
   */
  handleMessageInput() {
    const { messageField } = this.refs;
    if (!(messageField instanceof HTMLTextAreaElement)) return;
    if (messageField.value !== this.#lastAutoMessage) {
      this.#userEditedMessage = true;
    }
  }

  /**
   * @param {CustomEvent<{resource: object | null, data: {productId: string}}>} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail?.data?.productId !== this.dataset.productId) return;

    if (event.detail.resource == null) {
      this.#show();
    } else {
      this.setAttribute('hidden', '');
    }
  };

  /**
   * Reveals the card (if hidden) and refreshes its pre-fill, whether that's because the current
   * selection matches no real variant, or because the shopper manually opened it via the
   * "Don't see what you're looking for?" trigger regardless of what's currently selected.
   */
  #show = () => {
    const wasHidden = this.hasAttribute('hidden');
    this.removeAttribute('hidden');
    this.#refreshMessage();
    // Only scroll on the transition into view, not on every subsequent selection change while
    // the card is already visible (that would keep yanking the page around).
    if (wasHidden) {
      this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  /**
   * Rewrites the pre-filled message textarea from the current live selection, unless the
   * customer has already started typing their own message.
   */
  #refreshMessage() {
    if (this.#userEditedMessage) return;

    const { messageField } = this.refs;
    if (!(messageField instanceof HTMLTextAreaElement)) return;

    const picker = this.#findVariantPicker();
    const pairs = picker ? getSelectedOptionPairs(picker) : [];
    const title = this.dataset.productTitle || '';

    let message = `I'm interested in a custom quote for ${title}.`;
    if (pairs.length > 0) {
      const optionsText = pairs.map(([name, value]) => `${name}: ${value}`).join(', ');
      message += ` Selected options: ${optionsText}.`;
    }

    messageField.value = message;
    this.#lastAutoMessage = message;
  }

  /**
   * Finds the live variant-picker for the same product and section as this form.
   * @returns {Element | null}
   */
  #findVariantPicker() {
    const sectionId = this.dataset.sectionId;
    const productId = this.dataset.productId;

    const scoped = document.querySelector(
      `variant-picker[data-section-id="${sectionId}"][data-product-id="${productId}"]`
    );
    if (scoped) return scoped;

    return document.querySelector(`variant-picker[data-product-id="${productId}"]`);
  }
}

if (!customElements.get('custom-quote-form')) {
  customElements.define('custom-quote-form', CustomQuoteForm);
}
